import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Paper,
  Stack,
  Chip,
  Fade,
  Slide
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { CREATE_WABA_CONNECTION_STATUS, GET_WABA_CONNECTION_STATUS } from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider';
import { useBranding } from '../../hooks/brandingProvider';
import botbridgeLogo from '../../assets/images/logo.webp';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// dialogOnly: open the modal straight away without the launcher card (used by Channel Onboarding)
const WhatsAppConnectionFlow = ({ onConnectionComplete, dialogOnly = false, onClose }) => {
  const { getValidToken } = useAuth();
  const { favicon_url: brandIcon } = useBranding();
  const [open, setOpen] = useState(dialogOnly);
  const [step, setStep] = useState('initial');
  const [embeddedLink, setEmbeddedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchCreateApp = async () => {
    try {
      setLoading(true);
      setError('');
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      
      // Make POST request to create app and get embed link
      const response = await fetch(CREATE_WABA_CONNECTION_STATUS, {
        method: 'POST', // Make sure it's a POST request
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-ID": tenant,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Create App Response:", data);
      
      // Extract the embed_link from the response
      if (data.embed_link) {
        setEmbeddedLink(data.embed_link);
        return data.embed_link;
      } else {
        throw new Error('No embed link received from server');
      }
      
    } catch (error) {
      console.error("Error creating WhatsApp app:", error);
      setError(error.message || 'Failed to create WhatsApp connection');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setStep('initial');
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
    setTimeout(() => {
      setStep('initial');
      setEmbeddedLink('');
      setError('');
    }, 300);
  };

  const handleConnect = async () => {
    setStep('loading');
    setError('');
    
    try {
      // Fetch the embed link from the backend
      const link = await fetchCreateApp();
      setEmbeddedLink(link);
      setStep('embedded-link');
    } catch (error) {
      console.error('Failed to create WhatsApp connection:', error);
      setStep('initial');
      // You might want to show an error alert here
    }
  };

  const handleOpenEmbeddedLink = () => {
    if (embeddedLink) {
      window.open(embeddedLink, '_blank', 'width=800,height=600');
    } else {
      setError('No embed link available');
    }
  };

  const handleMetaProcessComplete = () => {
    setStep('verify-ready');
  };

  const handleVerifyStatus = async () => {
    try {
      setError('');
      setStep('verifying');

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(GET_WABA_CONNECTION_STATUS, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-ID": tenant,
        },
      });

      const data = await response.json();

      console.log("Verify Status Response:", data);

      if (!response.ok || data.status !== "success") {
        setError(data.message || "Failed to verify WhatsApp connection");
        setStep("verify-ready");
        return;
      }

      // Show success animation
      setStep("success");

      // Close modal + refresh dashboard
      setTimeout(() => {
        handleClose();
        if (onConnectionComplete) {
          onConnectionComplete();
        }
      }, 2000);

    } catch (err) {
      console.error("Verify Status Error:", err);
      setError(err.message || "Something went wrong");
      setStep("verify-ready");
    }
  };

  return (
    <>
    {!dialogOnly && (
    <Box
      sx={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      {/* Dashboard Card */}
      <Paper
        elevation={8}
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 4,
          borderRadius: 3,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}
        >
          <PhoneIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>

        <Typography variant="h4" fontWeight="bold" gutterBottom>
          WhatsApp Business
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Connect your business number to start messaging customers
        </Typography>

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleOpen}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #00B0D7 90%)',
            }
          }}
        >
          Connect WhatsApp Number
        </Button>

        <Paper
          variant="outlined"
          sx={{ mt: 3, p: 2, bgcolor: 'primary.50', textAlign: 'left' }}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Why Connect?
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              ✓ Reach customers on their favorite app
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ✓ Automated messaging & responses
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ✓ Business profile verification
            </Typography>
          </Stack>
        </Paper>
      </Paper>
    </Box>
    )}

      {/* Modal Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 480
          }
        }}
      >
        {/* Close Button */}
        {step !== 'loading' && step !== 'verifying' && step !== 'success' && (
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        <DialogContent sx={{ p: 4, pt: 5 }}>
          
          {/* Step 1: Initial Connect Screen */}
          {step === 'initial' && (
            <Fade in={true}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Connect your number with WhatsApp Business API
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    bgcolor: 'primary.50',
                    borderRadius: 2,
                    p: 3,
                    my: 3
                  }}
                >
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Paper
                      elevation={2}
                      sx={{
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        transition: 'transform 0.3s',
                        '&:hover': { transform: 'scale(1.1)' }
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="28"
                          height="28"
                          viewBox="0 0 256 256"
                          fill="white"
                          style={{ display: 'block' }}
                        >
                          <path d="M184 208c-13 0-25-9-39-28l-28-38-28 38c-14 19-26 28-39 28-29 0-50-33-50-80S21 48 50 48c19 0 36 15 55 45 5 7 10 15 15 23 5-8 10-16 15-23 19-30 36-45 55-45 29 0 50 33 50 80s-21 80-50 80zm-39-64 21 29c11 14 19 21 23 21 14 0 26-24 26-56s-12-56-26-56c-10 0-23 13-39 39l-5 7zm-57-27c-16-26-29-39-39-39-14 0-26 24-26 56s12 56 26 56c4 0 12-7 23-21l21-29 5-7z"/>
                        </svg>

                      </Box>
                    </Paper>

                    <Paper
                      elevation={2}
                      sx={{
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        transition: 'transform 0.3s',
                        '&:hover': { transform: 'scale(1.1)' }
                      }}
                    >
                      <WhatsAppIcon sx={{ color: 'success.main', fontSize: 32 }} />
                    </Paper>

                    <Paper
                      elevation={2}
                      sx={{
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        transition: 'transform 0.3s',
                        '&:hover': { transform: 'scale(1.1)' }
                      }}
                    >
                      <Box
                        component="img"
                        src={brandIcon || botbridgeLogo}
                        alt="BotBridge Logo"
                        sx={{ 
                          width: 32,
                          height: 32,
                          objectFit: 'contain'
                        }}
                      />
                    </Paper>
                  </Stack>
                </Box>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'success.50',
                      border: '1px solid',
                      borderColor: 'success.200'
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: 'success.main', mt: 0.5 }} />
                      <Typography variant="body2" fontWeight="500">
                        Keep your business phone number handy to receive OTP
                      </Typography>
                    </Stack>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'success.50',
                      border: '1px solid',
                      borderColor: 'success.200'
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: 'success.main', mt: 0.5 }} />
                      <Typography variant="body2" fontWeight="500">
                        If you are currently using WhatsApp on that number, create a backup and delete the account to continue
                      </Typography>
                    </Stack>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'success.50',
                      border: '1px solid',
                      borderColor: 'success.200'
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: 'success.main', mt: 0.5 }} />
                      <Typography variant="body2" fontWeight="500">
                        Keep your login credentials to the Facebook Account ready where you want to setup WhatsApp for Business
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleConnect}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Connect'}
                  </Button>
                </Stack>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button size="small" sx={{ textTransform: 'none' }}>
                    Help Docs →
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Step 2: Loading State */}
          {step === 'loading' && (
            <Fade in={true}>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {loading ? 'Creating WhatsApp App...' : 'Generating Setup Link'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we prepare your WhatsApp Business connection...
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Step 3: Embedded Link Generated */}
          {step === 'embedded-link' && (
            <Fade in={true}>
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'success.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                  </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Setup Link Generated
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click below to complete the setup process with Meta
                  </Typography>
                </Box>

                <Alert severity="warning" sx={{ mb: 3 }}>
                  <AlertTitle sx={{ fontWeight: 'bold' }}>Important</AlertTitle>
                  Upon click, you'll be redirected to complete steps via Meta. 
                  Follow the instructions and once the process is complete, 
                  <strong> return here</strong> and click on "Completed Meta Process".
                </Alert>

                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<OpenInNewIcon />}
                    onClick={handleOpenEmbeddedLink}
                    sx={{ py: 1.5 }}
                    disabled={!embeddedLink}
                  >
                    Open Meta Setup
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleMetaProcessComplete}
                  >
                    Completed Meta Process
                  </Button>
                </Stack>

                {embeddedLink && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Link: {embeddedLink.substring(0, 50)}...
                  </Typography>
                )}
              </Box>
            </Fade>
          )}

          {/* Rest of your steps remain the same */}
          {/* Step 4: Ready to Verify */}
          {step === 'verify-ready' && (
            <Fade in={true}>
              <Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'primary.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <PhoneIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Ready to Verify
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click below to verify your WhatsApp Business number status
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <Button variant="contained" size="large" fullWidth onClick={handleVerifyStatus}>
                    Verify Status
                  </Button>
                  <Button variant="outlined" size="large" fullWidth onClick={handleClose}>
                    Cancel
                  </Button>
                </Stack>
              </Box>
            </Fade>
          )}

          {/* Step 5: Verifying */}
          {step === 'verifying' && (
            <Fade in={true}>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Verifying Status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Checking your WhatsApp Business connection...
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Step 6: Success */}
          {step === 'success' && (
            <Fade in={true}>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    bgcolor: 'success.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
                </Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Successfully Connected!
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Your number has been successfully onboarded on Meta
                </Typography>
                <Chip
                  label="Refreshing dashboard..."
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Fade>
          )}
          
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WhatsAppConnectionFlow;